import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  BookOpen,
  BookMarked,
  Receipt,
  ClipboardList,
  LogOut,
  X,
  GraduationCap,
  Megaphone,
  MessagesSquare,
} from "lucide-react";
import { logoutUserThunk } from "../../../auth/authThunk";
import "../lms.css";

const STUDENT_NAV_ITEMS = [
  {
    section: "STUDENT MENU",
    items: [
      {
        label: "Courses",
        icon: BookOpen,
        to: "/student-lms/student-courses/available-courses",
        matchCheck: (pathname) =>
          pathname.includes("available-courses") ||
          pathname.includes("enrolled-courses") ||
          pathname.includes("requested-courses") ||
          pathname.includes("/student-lms/courses/")
      },
      {
        label: "Exams",
        icon: ClipboardList,
        to: "/student-lms/exams",
        matchCheck: (pathname) => pathname.includes("/student-lms/exams"),
      },
      {
        label: "My Attendance",
        icon: ClipboardList,
        to: "/student-lms/attendance",
        matchCheck: (pathname) => pathname.includes("/student-lms/attendance"),
      },
      {
        label: "Payment History",
        icon: Receipt,
        to: "/student-lms/student-courses/payment-history",
        matchCheck: (pathname) => pathname.includes("payment-history"),
      },
      {
        label: "Communications",
        icon: MessagesSquare,
        children: [
          { label: "Notice Board/Chat", icon: Megaphone, to: "/student-lms/notice-board" },
        ],
      },
    ],
  },
];

function getInitials(name = "") {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function StudentLmsSidebar({ isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogout = async () => {
    await dispatch(logoutUserThunk());
    localStorage.removeItem("userData");
    localStorage.removeItem("userToken");
    navigate("/login");
  };

  const userData = (() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      return {};
    }
  })();

  const userName = userData?.admin_name || userData?.school_name || "Student";
  const schoolName = userData?.school_name || "";
  const userRole = "Student";

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <aside className={`lms-sidebar ${isOpen ? "lms-sidebar--open" : ""}`}>
      {/* Logo + mobile close */}
      <div className="lms-sidebar-logo">
        <div className="lms-logo-icon">
          <GraduationCap size={20} color="#ffffff" />
        </div>
        <div className="lms-logo-text">
          <span className="lms-logo-title">DocMaster LMS</span>
          <span className="lms-logo-sub">STUDENT</span>
        </div>
        <button
          className="lms-sidebar-close"
          onClick={onClose}
          title="Close menu"
        >
          <X size={16} strokeWidth={2} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="lms-sidebar-nav">
        {STUDENT_NAV_ITEMS.map((group) => (
          <div key={group.section}>
            <div className="lms-nav-section-label">{group.section}</div>
            {group.items.map((item) => {
              const Icon = item.icon;

              if (item.children) {
                return (
                  <div key={item.label}>
                    <div className="lms-nav-item" style={{ cursor: "default", opacity: 0.85 }}>
                      <span className="lms-nav-icon">
                        <Icon size={16} strokeWidth={2} />
                      </span>
                      <span className="lms-nav-label">{item.label}</span>
                    </div>
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = location.pathname.startsWith(child.to);
                      return (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          className={`lms-nav-item ${childActive ? "active" : ""}`}
                          onClick={handleNavClick}
                          style={{ paddingLeft: "34px" }}
                        >
                          <span className="lms-nav-icon">
                            <ChildIcon size={14} strokeWidth={2} />
                          </span>
                          <span className="lms-nav-label">{child.label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                );
              }

              const isActive = item.matchCheck
                ? item.matchCheck(location.pathname)
                : location.pathname.startsWith(item.to);

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={`lms-nav-item ${isActive ? "active" : ""}`}
                  onClick={handleNavClick}
                >
                  <span className="lms-nav-icon">
                    <Icon size={16} strokeWidth={2} />
                  </span>
                  <span className="lms-nav-label">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User info */}
      <div className="lms-sidebar-user">
        <div className="lms-user-avatar">{getInitials(userName)}</div>
        <div className="lms-user-info">
          <div className="lms-user-name">{userName}</div>
          <div className="lms-user-role">{userRole}</div>
          {schoolName && (
            <div className="lms-user-school" title={schoolName}>
              {schoolName}
            </div>
          )}
        </div>
        <button
          className="lms-logout-btn"
          onClick={handleLogout}
          title="Logout"
        >
          <LogOut size={15} strokeWidth={2} />
        </button>
      </div>
    </aside>
  );
}
